import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ChatWidget from '../components/ChatWidget';
import SchedulerHeader from '../components/scheduler/SchedulerHeader';
import ScheduleList from '../components/scheduler/ScheduleList';
import ConfirmModal from '../components/scheduler/ConfirmModal';
import { EmptyState, LoadingOverlay, useToast } from '../components/ui';
import type { ScheduledTask, Workspace, PageKey } from '../types';
import {
  getSchedulerTasks,
  deleteSchedulerTask,
  executeTaskNow,
  toggleScheduler,
  reconcileSchedulerTasks,
  getWorkspace,
} from '../api';

type SchedulePageProps = {
  workspaceId: number;
  onNavigate?: (_page: PageKey) => void;
};

const STEP_PHRASE: Record<ScheduledTask['task_type'], string> = {
  CREATE_ITEM: 'write the content for this post',
  CREATE_MEDIA: 'generate the media for this post',
  PUBLISH: 'publish this post to Instagram',
};

const STEP_NAME: Record<ScheduledTask['task_type'], string> = {
  CREATE_ITEM: 'Write content',
  CREATE_MEDIA: 'Generate media',
  PUBLISH: 'Publish',
};

const STATUS_NAME: Record<ScheduledTask['status'], string> = {
  pending: 'Pending',
  running: 'Running',
  done: 'Done',
  failed: 'Failed',
  canceled: 'Canceled',
  blocked: 'Blocked',
};

type ReconcileResult = Awaited<ReturnType<typeof reconcileSchedulerTasks>>;

const formatReconcileSummary = (result: ReconcileResult) => {
  const checkedSummary = `Checked ${result.checked} eligible task${result.checked === 1 ? '' : 's'}.`;
  const typeSummary = [
    `${result.reconciled_create_item} content`,
    `${result.reconciled_create_media} media`,
    `${result.reconciled_publish} publish`,
  ].join(', ');

  return `${result.message} ${checkedSummary} Updated: ${typeSummary}.`;
};

function SchedulePage({ workspaceId, onNavigate }: SchedulePageProps) {
  const navigate = useNavigate();
  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [tasks, setTasks] = useState<ScheduledTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isReconciling, setIsReconciling] = useState(false);
  const [lastReconcileSummary, setLastReconcileSummary] = useState<string | null>(null);

  // Confirm dialogs
  const [runTask, setRunTask] = useState<ScheduledTask | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [removeTask, setRemoveTask] = useState<ScheduledTask | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);

  const { toast } = useToast();
  const autoReconciledWorkspaceId = useRef<number | null>(null);

  const fetchData = useCallback(async (options?: { reconcileFirst?: boolean }) => {
    try {
      if (
        options?.reconcileFirst &&
        autoReconciledWorkspaceId.current !== workspaceId
      ) {
        autoReconciledWorkspaceId.current = workspaceId;

        try {
          const result = await reconcileSchedulerTasks(workspaceId);
          setLastReconcileSummary(`Auto sync: ${formatReconcileSummary(result)}`);
        } catch (err) {
          autoReconciledWorkspaceId.current = null;
          console.error('Failed to auto-sync scheduler tasks:', err);
        }
      }

      const [workspaceData, tasksData] = await Promise.all([
        getWorkspace(workspaceId),
        getSchedulerTasks(workspaceId),
      ]);
      setWorkspace(workspaceData);
      setTasks(tasksData.results || []);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch scheduler data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load scheduler data');
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    setLoading(true);
    fetchData({ reconcileFirst: true });
    // Poll every 30 seconds so statuses stay fresh as the scheduler runs.
    const interval = setInterval(() => fetchData(), 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleToggleScheduler = async (enabled: boolean) => {
    if (!workspace) return;
    try {
      await toggleScheduler(workspaceId, enabled);
      setWorkspace({ ...workspace, scheduler_enabled: enabled });
    } catch (err) {
      console.error('Failed to toggle scheduler:', err);
      toast('Failed to toggle scheduler', 'error');
    }
  };

  const handleReconcileTasks = async () => {
    if (isReconciling) return;
    try {
      setIsReconciling(true);
      const result = await reconcileSchedulerTasks(workspaceId);
      const summary = formatReconcileSummary(result);

      setLastReconcileSummary(summary);
      toast(summary, result.reconciled > 0 ? 'success' : 'info');
      await fetchData();
    } catch (err) {
      console.error('Failed to reconcile tasks:', err);
      toast(err instanceof Error ? err.message : 'Failed to reconcile tasks', 'error');
    } finally {
      setIsReconciling(false);
    }
  };

  const confirmRunTask = async () => {
    if (!runTask) return;
    const dependencyTask = runTask.depends_on
      ? tasks.find((t) => t.id === runTask.depends_on)
      : undefined;
    if (dependencyTask && dependencyTask.status !== 'done') {
      toast('This step is blocked until its prerequisite is done.', 'error');
      return;
    }
    try {
      setIsRunning(true);
      const result = await executeTaskNow(runTask.id);
      toast(result.message || 'Step started.', 'success');
      setRunTask(null);
      await fetchData();
    } catch (err) {
      console.error('Failed to execute task:', err);
      toast(err instanceof Error ? err.message : 'Failed to run this step', 'error');
    } finally {
      setIsRunning(false);
    }
  };

  const confirmRemovePost = async () => {
    if (!removeTask?.outline) {
      setRemoveTask(null);
      return;
    }
    const outlineId = removeTask.outline;
    const outlineTasks = tasks.filter((t) => t.outline === outlineId);

    try {
      setIsRemoving(true);
      const results = await Promise.allSettled(
        outlineTasks.map((t) => deleteSchedulerTask(t.id))
      );
      const failed = results.filter((r) => r.status === 'rejected').length;
      const deleted = results.length - failed;

      setRemoveTask(null);
      await fetchData();

      if (failed > 0) {
        toast(`Removed ${deleted} step${deleted === 1 ? '' : 's'}, ${failed} failed.`, 'error');
      } else {
        toast('Post removed from the schedule.', 'success');
      }
    } catch (err) {
      console.error('Failed to remove post:', err);
      toast(err instanceof Error ? err.message : 'Failed to remove post', 'error');
    } finally {
      setIsRemoving(false);
    }
  };

  const handleNavigateToPost = (outlineId: number) => {
    navigate(`/content/${outlineId}`);
  };

  // Earliest still-pending task — drives the header's "next action" line.
  const nextTask = tasks
    .filter((t) => t.status === 'pending')
    .sort((a, b) => new Date(a.scheduled_for).getTime() - new Date(b.scheduled_for).getTime())[0];

  const header = (
    <div className="page-header">
      <h1 className="page-header__title">Scheduler</h1>
      <p className="page-header__subtitle">
        Every post you schedule becomes three steps — write the content, generate the media, then
        publish — and Conistra runs each one automatically at the right time. Here's what's coming up
        and what's already done.
      </p>
    </div>
  );

  if (loading) {
    return (
      <div>
        {header}
        <LoadingOverlay label="Loading your schedule…" />
      </div>
    );
  }

  if (!workspace) {
    return (
      <div>
        {header}
        <EmptyState
          icon="⚠️"
          title="Workspace not found"
          description="Unable to load workspace data. Please try refreshing."
        />
      </div>
    );
  }

  const schedulerEnabled = workspace.scheduler_enabled ?? true;
  const runTaskDependency = runTask?.depends_on
    ? tasks.find((t) => t.id === runTask.depends_on)
    : undefined;
  const canRunSelectedTask = !runTaskDependency || runTaskDependency.status === 'done';

  return (
    <div>
      {header}

      {error && (
        <div className="page-error" role="alert">
          <span aria-hidden="true">⚠</span> {error}
        </div>
      )}

      {schedulerEnabled === false && (
        <div className="scheduler-paused-banner">
          ⏸ Scheduler is paused. Steps stay scheduled but won't run until you turn it back on.
        </div>
      )}

      <SchedulerHeader
        workspace={workspace}
        onToggleScheduler={handleToggleScheduler}
        onReconcileTasks={handleReconcileTasks}
        isReconcileLoading={isReconciling}
        reconcileSummary={lastReconcileSummary}
        nextActionTime={nextTask?.scheduled_for}
        onNavigateToSettings={onNavigate ? () => onNavigate('settings') : undefined}
      />

      {tasks.length === 0 ? (
        <EmptyState
          icon="🗓️"
          title="No scheduled posts yet"
          description="Give a post a publish date and its three steps appear here automatically."
        />
      ) : (
        <ScheduleList
          tasks={tasks}
          schedulerEnabled={schedulerEnabled}
          onRunTask={setRunTask}
          onRemovePost={setRemoveTask}
          onNavigateToPost={handleNavigateToPost}
        />
      )}

      <ConfirmModal
        isOpen={Boolean(runTask)}
        title="Run this step now?"
        confirmLabel={canRunSelectedTask ? 'Run now' : 'Blocked by dependency'}
        confirmDisabled={!canRunSelectedTask}
        loading={isRunning}
        onConfirm={confirmRunTask}
        onClose={() => (isRunning ? undefined : setRunTask(null))}
      >
        {runTask && (
          <>
            <p>
              This will {STEP_PHRASE[runTask.task_type]} immediately, ahead of its scheduled time.
            </p>
            {runTaskDependency && runTaskDependency.status !== 'done' && (
              <p className="modal-note">
                This step depends on {STEP_NAME[runTaskDependency.task_type].toLowerCase()} first.
                Current prerequisite status: {STATUS_NAME[runTaskDependency.status]}.
              </p>
            )}
            <p className="modal-note">
              {STEP_NAME[runTask.task_type]} · {runTask.outline_title || 'Untitled post'}
            </p>
          </>
        )}
      </ConfirmModal>

      <ConfirmModal
        isOpen={Boolean(removeTask)}
        title="Remove this post from the schedule?"
        confirmLabel="Remove post"
        confirmVariant="danger"
        loading={isRemoving}
        onConfirm={confirmRemovePost}
        onClose={() => (isRemoving ? undefined : setRemoveTask(null))}
      >
        {removeTask && (
          <p>
            This deletes all three steps for{' '}
            <strong>{removeTask.outline_title || 'this post'}</strong> — write content, generate
            media, and publish. This can't be undone, and it does not delete the post itself.
          </p>
        )}
      </ConfirmModal>

      <ChatWidget workspaceId={workspaceId} />
    </div>
  );
}

export default SchedulePage;
